import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pill, Clock, UtensilsCrossed, ChevronRight, Sparkles, Loader2, ShoppingCart, CheckCircle2 } from "lucide-react";
import SupplementProductDetail, { formatProductTitle } from "@/components/supplements/SupplementProductDetail";
import { TIMING_COLORS, normalizePlan, normalizePlanTiming } from "@/lib/supplementTiming";

export default function MySupplements() {
  const [plans, setPlans] = useState([]);
  const [dietSources, setDietSources] = useState([]);
  const [adherence, setAdherence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingCheck, setSavingCheck] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [aiHints, setAiHints] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [hintsOpen, setHintsOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const requests = [
      { label: "plans compléments", promise: api.get("/supplements/my-plans"), apply: (data) => setPlans((data || []).map(normalizePlan)) },
      { label: "sources diet", promise: api.get("/supplements/diet-sources"), apply: (data) => setDietSources(data || []) },
      { label: "checklist", promise: api.get("/supplements/adherence/today"), apply: (data) => setAdherence(data) },
    ];

    const results = await Promise.allSettled(requests.map((r) => r.promise));
    const failures = [];

    results.forEach((result, index) => {
      const { label, apply } = requests[index];
      if (result.status === "fulfilled") {
        apply(result.value.data);
      } else {
        const err = result.reason;
        const detail = err?.response?.data?.detail || err?.response?.data?.message || err?.message || "erreur inconnue";
        failures.push(`${label} (${detail})`);
      }
    });

    if (failures.length > 0) {
      toast.error(`Erreur de chargement : ${failures.join(" · ")}`);
    }

    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleCheck = async (itemId, checked) => {
    if (!adherence) return;
    const nextChecks = { ...adherence.checks, [itemId]: checked };
    setAdherence((a) => ({ ...a, checks: nextChecks }));
    setSavingCheck(true);
    try {
      const { data } = await api.put("/supplements/adherence/today", { checks: nextChecks });
      setAdherence((a) => ({ ...a, ...data }));
    } catch {
      toast.error("Impossible d'enregistrer");
      load();
    } finally {
      setSavingCheck(false);
    }
  };

  const fetchAiHints = async () => {
    setAiLoading(true);
    setHintsOpen(true);
    setAiHints(null);
    try {
      const { data } = await api.post("/nutrition/meal-supplement-hints");
      setAiHints(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analyse IA impossible — renseignez d'abord votre journal repas");
      setHintsOpen(false);
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    );
  }

  const hasContent = plans.length > 0 || dietSources.length > 0;
  const checklistItems = adherence?.items || [];

  return (
    <div data-testid="my-supplements-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-indigo-100 flex items-center justify-center">
            <Pill className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Mes Compléments</h1>
            <p className="text-slate-400 mt-1 text-sm">Produits recommandés par votre coach avec fiches détaillées</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button asChild variant="outline" size="sm" className="gap-1.5">
            <Link to="/student/shopping-list"><ShoppingCart className="w-4 h-4" /> Liste de courses</Link>
          </Button>
          {plans.length > 0 && (
            <Button size="sm" onClick={fetchAiHints} disabled={aiLoading} className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white">
              {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Ajuster selon mes repas
            </Button>
          )}
        </div>
      </div>

      {checklistItems.length > 0 && (
        <Card className="mt-6 border-indigo-200 bg-indigo-50/30">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div>
                <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-indigo-600" />
                  Checklist du jour
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {adherence?.completed_count ?? 0}/{adherence?.total_count ?? 0} pris
                  {adherence?.is_complete ? " — journée complète !" : ""}
                </p>
              </div>
              {savingCheck && <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />}
            </div>
            <div className="space-y-2">
              {checklistItems.map((item) => {
                const timing = normalizePlanTiming(item.timing, item.notes);
                const checked = !!adherence?.checks?.[item.item_id];
                return (
                  <label
                    key={item.item_id}
                    className={`flex items-center gap-3 rounded-xl px-3 py-3 cursor-pointer transition-colors ${
                      checked ? "bg-green-50 border border-green-100" : "bg-white border border-slate-100"
                    }`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => toggleCheck(item.item_id, !!v)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${checked ? "text-green-800 line-through decoration-green-400" : "text-slate-900"}`}>
                        {item.name}
                      </p>
                      <p className="text-xs text-slate-500">{item.dosage} · {timing}</p>
                    </div>
                    <Badge className={`shrink-0 text-[10px] ${TIMING_COLORS[timing] || ""}`}>{timing}</Badge>
                  </label>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {!hasContent ? (
        <div className="text-center py-16 mt-8">
          <Pill className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Aucun complément assigné pour le moment</p>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {plans.map((plan) => (
            <Card key={plan.plan_id} className="border-indigo-100">
              <CardContent className="p-6">
                <h2 className="font-heading text-xl font-bold text-slate-900">{plan.title}</h2>
                {plan.notes && <p className="text-sm text-slate-500 mt-1">{plan.notes}</p>}
                <div className="mt-4 space-y-3">
                  {plan.items?.map((item) => {
                    const timing = normalizePlanTiming(item.timing, item.notes);
                    return (
                      <button
                        key={item.item_id}
                        type="button"
                        onClick={() => item.supplement && setDetailItem({ ...item, timing })}
                        className={`w-full text-left flex items-center gap-4 bg-slate-50 rounded-xl p-4 transition-colors ${
                          item.supplement ? "hover:bg-indigo-50/60 cursor-pointer" : ""
                        }`}
                      >
                        {item.supplement ? (
                          <div className="flex-1 min-w-0">
                            <SupplementProductDetail product={item.supplement} compact />
                            <p className="text-sm text-indigo-700 font-medium mt-2 ml-[4.25rem]">
                              {item.dosage}
                              {timing ? (
                                <span className="text-slate-600 font-normal"> · {timing}</span>
                              ) : null}
                            </p>
                            {item.notes && (
                              <p className="text-xs text-slate-400 mt-0.5 ml-[4.25rem]">{item.notes}</p>
                            )}
                          </div>
                        ) : (
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
                                <Pill className="w-5 h-5 text-indigo-600" />
                              </div>
                              <div>
                                <p className="font-semibold text-slate-900">{item.name}</p>
                                <p className="text-sm text-slate-500">
                                  {item.dosage}
                                  {timing ? ` · ${timing}` : ""}
                                </p>
                                {item.notes && <p className="text-xs text-slate-400 mt-0.5">{item.notes}</p>}
                              </div>
                            </div>
                          </div>
                        )}
                        <Badge className={`shrink-0 ${TIMING_COLORS[timing] || "bg-slate-100 text-slate-600"}`}>
                          <Clock className="w-3 h-3 mr-1" />{timing}
                        </Badge>
                        {item.supplement && <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}

          {dietSources.map((src) => (
            <Card key={src.diet_plan_id} className="border-green-100">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <UtensilsCrossed className="w-5 h-5 text-green-600" />
                  <h2 className="font-semibold text-slate-900">{src.diet_plan_title}</h2>
                  <Badge variant="outline" className="text-xs">Plan diététique</Badge>
                </div>
                <div className="space-y-2">
                  {(src.supplements || []).map((sup, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm bg-green-50 rounded-lg px-3 py-2">
                      <Pill className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-slate-900">{typeof sup === "string" ? sup : sup.name || sup}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={hintsOpen} onOpenChange={setHintsOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Compléments & repas réels
            </DialogTitle>
          </DialogHeader>
          {aiLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-purple-500" /></div>
          ) : aiHints ? (
            <div className="space-y-4 mt-2">
              {aiHints.resume && <p className="text-sm text-slate-600">{aiHints.resume}</p>}
              {(aiHints.suggestions || []).map((s, i) => (
                <div key={i} className="rounded-xl border border-purple-100 bg-purple-50/50 p-4 text-sm">
                  <p className="font-semibold text-slate-900">{s.supplement_name}</p>
                  <p className="text-indigo-700 mt-1">
                    {s.current_timing} → <strong>{s.suggested_timing}</strong>
                    {s.link_to_meal ? ` (${s.link_to_meal})` : ""}
                  </p>
                  <p className="text-slate-600 mt-2">{s.reason}</p>
                </div>
              ))}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailItem} onOpenChange={(open) => !open && setDetailItem(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {detailItem?.supplement ? formatProductTitle(detailItem.supplement) : "Fiche produit"}
            </DialogTitle>
          </DialogHeader>
          {detailItem?.supplement && (
            <div className="space-y-4">
              <SupplementProductDetail product={detailItem.supplement} />
              <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-indigo-500 mb-1">Votre prescription</p>
                <p className="text-sm font-medium text-slate-900">{detailItem.dosage}</p>
                <p className="text-sm text-slate-600 mt-1">{detailItem.timing}</p>
                {detailItem.notes && <p className="text-xs text-slate-500 mt-2">{detailItem.notes}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}